/**
 * Reported Posts Page
 *
 * Purpose: Admin page for managing reported posts
 */

import type { ReportedPost } from '@/domain/reports/entities/reported_post.entity'
import { useReportedPostsViewModel } from '../viewmodels/use_reported_posts_viewmodel'
import { Search, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { ConfirmDialog } from '@/features/common/components/ConfirmDialog'

export function ReportedPostsPageView() {
  const { state, isLoading, isDeleting, actions } = useReportedPostsViewModel()
  const {
    filteredReportedPosts,
    paginatedReportedPosts,
    deleteDialog,
    totalPages,
    startIndex,
    endIndex,
    searchTerm,
    entriesPerPage,
    currentPage,
    sortField,
    sortDirection,
  } = state

  const getSortIcon = (field: keyof ReportedPost) => {

    if (sortField !== field) {
      return <ArrowUpDown className="w-3 h-3 text-gray-400" />
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-gray-700" />
    ) : (
      <ArrowDown className="w-3 h-3 text-gray-700" />
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Reported Posts</h1>
        </div>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">Reported Posts</h1>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Controls */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Show</span>
            <select
              value={entriesPerPage}
              onChange={e => {
                actions.setPageSize(Number(e.target.value))
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-sm text-gray-700">entries</span>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="search" className="text-sm text-gray-700">
              Search:
            </label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="search"
                type="text"
                value={searchTerm}
                onChange={e => actions.setSearch(e.target.value)}
                placeholder="Search reported posts..."
                className="pl-8 pr-4 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('id')}
                >
                  <div className="flex items-center gap-1 w-full">
                    #{getSortIcon('id')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Image
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('content')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Post Content
                    {getSortIcon('content')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('postedBy')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Posted By
                    {getSortIcon('postedBy')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('reportedBy')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Reported By
                    {getSortIcon('reportedBy')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('reason')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Reason
                    {getSortIcon('reason')}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none transition-colors"
                  onClick={() => actions.handleSort('createdAt')}
                >
                  <div className="flex items-center gap-1 w-full">
                    Created Date
                    {getSortIcon('createdAt')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedReportedPosts.length > 0 ? (
                paginatedReportedPosts.map((post, index) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {sortField === 'id' ? post.id : startIndex + index + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {post.imageUrl ? (
                        <img
                          src={post.imageUrl}
                          alt="Post"
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => {
                            // Hide image if it fails to load
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                          }}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                          <span className="text-xs text-gray-400">No Image</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{post.content}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {post.postedBy}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {post.reportedBy}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{post.reason}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {post.createdAt}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <button
                        onClick={() => actions.openDeleteDialog(post.id, post.content)}
                        className="p-1 hover:bg-gray-100 rounded text-gray-600 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">
                    No data available in table
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {filteredReportedPosts.length > 0 ? startIndex + 1 : 0} to{' '}
            {Math.min(endIndex, filteredReportedPosts.length)} of {filteredReportedPosts.length}{' '}
            entries
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => actions.goToPreviousPage()}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => actions.changePage(page)}
                className={`px-3 py-1 text-sm border rounded ${
                  currentPage === page
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                style={
                  currentPage === page
                    ? { backgroundColor: '#019b9a', borderColor: '#019b9a' }
                    : undefined
                }
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => actions.goToNextPage()}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={actions.closeDeleteDialog}
        onConfirm={() => void actions.confirmDelete()}
        title="Delete Reported Post"
        message={`Are you sure you want to delete this reported post? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  )
}

